import axios from 'axios';
import { API_CONFIG } from '../config/api';

// Analytics API endpoints
const ANALYTICS_ENDPOINTS = {
  DASHBOARD_DATA: '/api/analytics/dashboard',
  SITE_PERFORMANCE: '/api/analytics/sites',
  PILLAR_ANALYSIS: '/api/analytics/pillars',
  TREND_DATA: '/api/analytics/trends',
  EXPORT_DATA: '/api/analytics/export',
  CUSTOM_REPORT: '/api/analytics/reports'
};

// Create axios instance for analytics
const analyticsClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  timeout: API_CONFIG.TIMEOUT,
  headers: API_CONFIG.HEADERS
});

// Analytics API methods
export const analyticsAPI = {
  // Get dashboard overview data
  getDashboardData: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await analyticsClient.get(`${ANALYTICS_ENDPOINTS.DASHBOARD_DATA}?${params}`);
    return response.data;
  },

  // Get site performance analytics
  getSitePerformance: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await analyticsClient.get(`${ANALYTICS_ENDPOINTS.SITE_PERFORMANCE}?${params}`);
    return response.data;
  },

  // Get pillar analysis data
  getPillarAnalysis: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await analyticsClient.get(`${ANALYTICS_ENDPOINTS.PILLAR_ANALYSIS}?${params}`);
    return response.data;
  },

  // Get trend analysis data
  getTrendData: async (filters = {}) => {
    const params = new URLSearchParams(filters);
    const response = await analyticsClient.get(`${ANALYTICS_ENDPOINTS.TREND_DATA}?${params}`);
    return response.data;
  },

  // Export analytics data
  exportData: async (format = 'csv', filters = {}) => {
    const params = new URLSearchParams({ ...filters, format });
    const response = await analyticsClient.get(`${ANALYTICS_ENDPOINTS.EXPORT_DATA}?${params}`, {
      responseType: 'blob'
    });
    return response.data;
  },

  // Generate custom report
  generateCustomReport: async (reportConfig) => {
    const response = await analyticsClient.post(ANALYTICS_ENDPOINTS.CUSTOM_REPORT, reportConfig);
    return response.data;
  },

  // Generate AI insights using Amazon Bedrock
  generateAIInsights: async (data, filters) => {
    const response = await analyticsClient.post('/api/analytics/ai-insights', {
      data,
      filters,
      timestamp: new Date().toISOString()
    });
    return response.data;
  }
};

// Mock data generator for development/testing
export const mockAnalyticsData = {
  getDashboardData: (filters) => {
    return Promise.resolve({
      kpis: {
        totalSites: 25,
        activeAssessments: 156,
        overallCompletion: 78,
        monthlyResponses: 2847,
        topPillar: { name: 'We Drive Flow', score: 89 },
        attentionNeeded: { site: 'Site Gamma', completion: 45 }
      },
      sitePerformance: [
        { name: 'Site Alpha', completion: 92, avgScore: 4.2, trend: 'up', lastUpdated: '2 hours ago' },
        { name: 'Site Beta', completion: 78, avgScore: 3.8, trend: 'down', lastUpdated: '1 day ago' },
        { name: 'Site Gamma', completion: 45, avgScore: 3.1, trend: 'up', lastUpdated: '3 hours ago' },
        { name: 'Site Delta', completion: 85, avgScore: 4.0, trend: 'stable', lastUpdated: '5 hours ago' }
      ],
      pillarPerformance: [
        { name: 'We Drive Flow', completion: 89, responses: 1250, avgScore: 4.1 },
        { name: 'We Develop People', completion: 82, responses: 1100, avgScore: 3.9 },
        { name: 'We Improve Everything', completion: 75, responses: 980, avgScore: 3.7 },
        { name: 'We Respect Everyone', completion: 88, responses: 1180, avgScore: 4.0 },
        { name: 'We Deliver Results', completion: 71, responses: 890, avgScore: 3.6 }
      ],
      trends: {
        completionTrend: [
          { month: 'Jan', completion: 65 },
          { month: 'Feb', completion: 70 },
          { month: 'Mar', completion: 75 },
          { month: 'Apr', completion: 78 }
        ]
      },
      lastUpdated: new Date().toISOString()
    });
  }
};

export default analyticsAPI;